import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, OpenAIConfig } from '../types';
import { handleStreamResponse } from '../utils/StreamHandler';
import { parseErrorResponse } from '../utils/ErrorHandler';

interface OpenAIStreamResponse {
  choices?: Array<{
    delta?: {
      content?: string;
    };
  }>;
}

export class OpenAIService extends BaseAIService {
  private config: OpenAIConfig;
  
  constructor(config: OpenAIConfig = {}) {
    super('openai');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.apiKey || !this.config.model) {
      return {
        text: '',
        error: 'OpenAI API 密钥或模型未配置'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      const url = this.config.apiUrl || 'https://api.openai.com/v1/chat/completions';
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.apiKey}`
      };
      
      const body = {
        model: this.config.model || 'gpt-3.5-turbo',
        messages,
        temperature: options.temperature ?? this.config.temperature ?? 0.7,
        max_tokens: options.maxTokens || this.config.maxTokens,
        top_p: options.topP ?? this.config.topP ?? 1,
        stream: !!options.onUpdate,
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        const text = await handleStreamResponse({
          url,
          headers,
          body,
          signal: options.abortSignal,
          onUpdate: options.onUpdate,
          errorPrefix: 'OpenAI 请求失败',
          parseContent: (data: unknown) => {
            const response = data as OpenAIStreamResponse;
            return response.choices?.[0]?.delta?.content || '';
          }
        });
        
        return { text };
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response);
        }
        
        const data = await response.json();
        return {
          text: data.choices[0]?.message?.content || ''
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }
} 